import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ListChecks, ArrowLeftRight, FileCheck2, PlugZap,
  Loader2, AlertCircle, ArrowRight, ShieldCheck, Webhook
} from 'lucide-react';
import { certificationApi } from '../../services/iso20022Api';

// Exchange groups, in the order they matter for certification.
const GROUPS = {
  SWITCH: {
    icon: ArrowLeftRight,
    title: 'Switch exchange — inbound tests (you → switch)',
    tone: 'primary',
    blurb:
      'Each of these sends a signed ISO 20022 message from your gateway to a switch endpoint and ' +
      'checks that the switch verifies your signature and returns the correct signed response. ' +
      'Passing them is the certification: they prove your DFSP can actually communicate with the switch.',
  },
  CALLBACK: {
    icon: Webhook,
    title: 'Participant callbacks — outbound tests (switch → you)',
    tone: 'primary',
    blurb:
      'The other certifying direction: the switch delivers switch-signed messages to the callback ' +
      'endpoints you configure on the Callback Endpoints page, and each case checks your system ' +
      'responds properly (an HTTP 2xx acknowledgement; for the inbound recall, a signed camt.029 answer; ' +
      'for the tampered callback, ISO-CB-004, a rejection with a non-2xx status). ' +
      'They score whenever a flow triggers the callback, or directly via the endpoint’s "Send test" button.',
  },
  MESSAGE: {
    icon: FileCheck2,
    title: 'Message construction — prerequisites',
    tone: 'gray',
    blurb:
      'These build and validate messages locally — no switch is involved, and you never send them ' +
      'to the switch. All optional: recommended groundwork that confirms you can produce ' +
      'schema-valid XML before you exchange anything, but not required for certification.',
  },
  TRANSPORT: {
    icon: PlugZap,
    title: 'Connectivity — infrastructure',
    tone: 'gray',
    blurb:
      'Transport-level connection to the switch (mutual TLS). Depends on Sandbox infrastructure ' +
      'that is not yet deployed, so it reports as blocked and is not counted against you.',
  },
};
// Settlement (DFSP↔DFSP funds movement) is a scheme-level concern outside what a
// participant certifies against the switch — removed from the suite in 2.0.0.

// Human description of what a switch-exchange case sends and expects.
function exchangeStory(c) {
  if (c.exchange === 'CALLBACK') {
    // The trust check inverts the rule, as the engine's case instructions do:
    // the callback's signed content was altered, so accepting it fails the case.
    if (c.caseId === 'ISO-CB-004') {
      return {
        send: `switch-signed ${c.messageType}, altered after signing → your ${c.callbackKey} endpoint`,
        expect: 'your endpoint verifies the signature and rejects it with a non-2xx status',
        endpoint: null,
      };
    }
    return {
      send: `switch-signed ${c.messageType} → your ${c.callbackKey} endpoint`,
      expect: 'your endpoint acknowledges with HTTP 2xx',
      endpoint: null,
    };
  }
  const endpoint = (c.switchEndpoint || '').replace('/api/switch/iso20022', '');
  switch (c.scenario) {
    case 'HAPPY':
      return { send: `signed ${c.messageType}`, expect: `signed pacs.002 (TxSts ${c.expectedStatus || 'ACSC'})`, endpoint };
    case 'TAMPER':
      return { send: `tampered ${c.messageType}`, expect: 'rejection — pacs.002 RJCT', endpoint };
    case 'TRUST':
      return { send: `signed ${c.messageType}`, expect: 'accepted from a registered cert; rejected from an unregistered key', endpoint };
    case 'LOOKUP':
      return { send: `signed ${c.messageType}`, expect: 'signed acmt.024 identification-verification report', endpoint };
    case 'REGISTER':
      return { send: `signed ${c.messageType}`, expect: 'signed acmt.010 approval + acmt.014 account report', endpoint };
    case 'REGISTER_REJECT':
      return { send: `signed but invalid ${c.messageType}`, expect: 'signed acmt.011 rejection + acmt.014 (signature still accepted)', endpoint };
    case 'INBOUND_RECALL':
      return { send: `signed ${c.messageType} answering the camt.056 delivered to your inbound-recall endpoint`, expect: 'HTTP 200 — resolution verified and scored', endpoint };
    default:
      return { send: c.messageType ? `signed ${c.messageType}` : 'a signed message', expect: 'the correct signed response', endpoint };
  }
}

export default function CertificationScope() {
  const [suite, setSuite]   = useState(null);
  const [error, setError]   = useState(null);

  useEffect(() => {
    certificationApi.getSuite()
      .then(setSuite)
      .catch(e => setError(e.response?.data?.message || e.message));
  }, []);

  const cases = suite?.cases || [];
  const byExchange = (ex) => cases.filter(c => c.exchange === ex);
  const certifyingRunnable = cases.filter(c =>
    (c.exchange === 'SWITCH' || c.exchange === 'CALLBACK') && c.runnable && c.obligation === 'MANDATORY');

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ListChecks className="w-6 h-6 text-primary-600" /> Certification scope
        </h1>
        <p className="text-sm text-gray-500 mt-1">
          What the AfricaNenda ISO 20022 certification actually proves — and which tests lead to it.
        </p>
      </div>

      {/* Objective */}
      <div className="bg-primary-50 border border-primary-200 rounded-xl p-5">
        <h2 className="text-sm font-semibold text-primary-900 flex items-center gap-2">
          <ShieldCheck className="w-4 h-4" /> What we are certifying
        </h2>
        <p className="text-sm text-primary-900 mt-2">
          That a participant DFSP can <strong>successfully exchange signed ISO 20022 XML messages with the
          switch — in both directions</strong>: send a correctly signed request to the right switch endpoint
          and accept its signed response (inbound), and properly receive and respond to the switch-signed
          callbacks the switch delivers to your own endpoints (outbound). Only these exchanged messages
          decide certification. Everything else is either groundwork that makes a clean exchange possible,
          or infrastructure the Sandbox hasn't stood up yet.
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-500 mt-0.5 shrink-0" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {!suite && !error && (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-6">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading the suite…
        </div>
      )}

      {suite && (
        <>
          {/* At-a-glance */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-primary-700">{certifyingRunnable.length}</p>
              <p className="text-xs text-gray-500 mt-0.5">certifying exchanges (inbound + outbound) you run today</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-900">{byExchange('MESSAGE').length}</p>
              <p className="text-xs text-gray-500 mt-0.5">message-build prerequisites</p>
            </div>
            <div className="bg-white border border-gray-200 rounded-xl p-4 text-center">
              <p className="text-2xl font-bold text-gray-400">
                {byExchange('TRANSPORT').length}
              </p>
              <p className="text-xs text-gray-500 mt-0.5">infrastructure — not counted against you</p>
            </div>
          </div>

          {/* Groups */}
          {Object.entries(GROUPS).map(([ex, g]) => {
            const list = byExchange(ex);
            if (list.length === 0) return null;
            const Icon = g.icon;
            const isSwitch = ex === 'SWITCH' || ex === 'CALLBACK';
            return (
              <div key={ex}
                className={`rounded-xl border p-5 ${isSwitch ? 'bg-white border-primary-200' : 'bg-white border-gray-200'}`}>
                <h2 className={`text-sm font-semibold flex items-center gap-2 ${isSwitch ? 'text-primary-800' : 'text-gray-900'}`}>
                  <Icon className={`w-4 h-4 ${isSwitch ? 'text-primary-600' : 'text-gray-400'}`} />
                  {g.title}
                  <span className="ml-1 text-xs font-normal text-gray-400">({list.length})</span>
                </h2>
                <p className="text-xs text-gray-500 mt-1 mb-3">{g.blurb}</p>

                <div className="space-y-2">
                  {list.map(c => {
                    const story = isSwitch ? exchangeStory(c) : null;
                    return (
                      <div key={c.caseId}
                        className={`rounded-lg p-3 border ${isSwitch ? 'border-primary-100 bg-primary-50/40' : 'border-gray-100 bg-gray-50'}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-xs text-gray-400">{c.caseId}</span>
                          <span className="text-sm font-medium text-gray-800 flex-1">{c.title}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            c.obligation === 'MANDATORY' ? 'bg-gray-200 text-gray-700' : 'bg-gray-100 text-gray-500'
                          }`}>{c.obligation.toLowerCase()}</span>
                          {!c.runnable && (
                            <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">blocked</span>
                          )}
                        </div>
                        {isSwitch && story && (
                          <div className="mt-2 flex items-center gap-2 text-xs text-gray-600 flex-wrap">
                            <span className="font-mono px-1.5 py-0.5 rounded bg-white border border-primary-100">{story.send}</span>
                            {story.endpoint && (
                              <span className="font-mono text-gray-400">POST {story.endpoint}</span>
                            )}
                            <ArrowRight className="w-3.5 h-3.5 text-gray-400" />
                            <span className="text-gray-700">{story.expect}</span>
                          </div>
                        )}
                        {!isSwitch && c.blockedReason && (
                          <p className="mt-1 text-xs text-gray-400">{c.blockedReason}</p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* CTA */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap">
            <p className="text-sm text-blue-900">
              Ready to prove your switch exchanges? Exercise them from your gateway, or run the built-in
              suite, on the Certification page.
            </p>
            <Link to="/iso20022/certification"
              className="shrink-0 flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700">
              Go to Certification <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </>
      )}
    </div>
  );
}
